import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Building2, Loader2, Search } from 'lucide-react'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  LUDOLIO_SUPPORT_EMAIL,
  SsoAccountLinkPanel,
  type SsoAccountLinkMode,
} from '@ludolio/shared-ui'
import { API_BASE_URL } from '@/config/api'
import { AppleIcon, WindowsIcon } from '@/PlatformIcons'

type SsoPortal = 'student' | 'teacher' | 'librarian'
type SsoClient = 'web' | 'desktop'
type RoleInvitationKind = 'instructor' | 'librarian'
type RoleInvitationAuthenticationMode = 'otp_only' | 'otp_and_sso' | 'sso_only'

type DiscoveryOrganization = {
  id: string
  name: string
  provider: string
  domains: string[]
  supportsWayfless: boolean
}

type DiscoveryResponse = {
  success: boolean
  data?: DiscoveryOrganization[]
  error?: string
  message?: string
}

type AccountLinkStatus = {
  provider: string
  portal: string
  client: string
  organizationId: string
  organizationName: string | null
  ssoEmail: string
  expiresAt: string
}

type RoleInvitationStatus = {
  status: 'active'
  kind: RoleInvitationKind
  organizationId: string
  organizationName: string
  invitedEmail: string
  invitedName: string
  expiresAt: string
  authenticationMode: RoleInvitationAuthenticationMode
} | {
  status: 'already_accepted'
  kind: RoleInvitationKind
  redirectUrl: string
}

type StudentInvitationStatus = {
  status: 'active'
  organizationId: string
  organizationName: string
  invitedEmail: string
  units: Array<{ id: string; code: string; name: string }>
  expiresAt: string
  authenticationMode: RoleInvitationAuthenticationMode
} | {
  status: 'already_accepted'
}

type StudentInvitationCompletion = {
  organizationName: string | null
  units: Array<{ id: string; code: string; name: string }>
}

type ApiErrorPayload = {
  success?: boolean
  error?: string
  message?: string
}

type SsoError = {
  code: string
  message: string
  provider: string | null
  authenticated: boolean
  reason: SsoDenialReason | null
  nextAction: SsoNextAction | null
}

type SsoDenialReason =
  | 'subscription_inactive'
  | 'organization_not_configured'
  | 'organization_mismatch'
  | 'organization_ambiguous'
  | 'missing_persistent_id'
  | 'missing_email'
  | 'missing_name'
  | 'missing_scope'
  | 'portal_access_denied'

type SsoNextAction =
  | 'choose_institution'
  | 'contact_institution_admin'
  | 'contact_institution_it'
  | 'contact_ludolio'

const denialMessages: Record<SsoDenialReason, string> = {
  subscription_inactive: 'You have successfully logged in, but your organization does not have a Ludolio subscription.',
  organization_not_configured: 'You have successfully logged in, but your organization is not yet set up for Ludolio access.',
  organization_mismatch: 'You have successfully logged in, but this account does not belong to the organization you selected.',
  organization_ambiguous: 'You have successfully logged in, but we could not determine which organization should provide access.',
  missing_persistent_id: 'You have successfully logged in, but your organization did not release the identifier required by Ludolio.',
  missing_email: 'You have successfully logged in, but your organization did not release the email address required by Ludolio.',
  missing_name: 'You have successfully logged in, but your organization did not release the name details required by Ludolio.',
  missing_scope: 'You have successfully logged in, but your organization did not release the scope required to verify access.',
  portal_access_denied: 'You have successfully logged in, but this account is not approved for this Ludolio portal.',
}

const denialGuidance: Record<SsoNextAction, string> = {
  choose_institution: 'Please choose another organization and try again.',
  contact_institution_admin: 'Please contact your organization’s administrator.',
  contact_institution_it: 'Please contact your organization’s IT team.',
  contact_ludolio: 'Please contact Ludolio support for help.',
}

const denialReasons = new Set<SsoDenialReason>(Object.keys(denialMessages) as SsoDenialReason[])
const nextActions = new Set<SsoNextAction>(Object.keys(denialGuidance) as SsoNextAction[])

function parsePortal(value: string | null): SsoPortal {
  if (value === 'teacher' || value === 'librarian' || value === 'student') return value
  return 'student'
}

function parseClient(value: string | null): SsoClient {
  return value === 'desktop' ? 'desktop' : 'web'
}

function parseSsoError(params: URLSearchParams): SsoError | null {
  const code = params.get('sso_error')
  if (!code) return null

  const fallbackMessages: Record<string, string> = {
    access_denied: 'Sign-in was cancelled or denied by your institution.',
    sso_access_denied: 'Your institution did not approve this sign-in, or your account is not allowed to access Ludolio.',
    sso_session_expired: 'Your sign-in session expired. Please choose your institution and try again.',
    sso_invalid_request: 'We could not start institutional sign-in. Please try again.',
    sso_failed: 'We could not complete institutional sign-in. Please try again.',
  }

  const message = params.get('sso_message')?.trim()
  const rawReason = params.get('sso_reason') as SsoDenialReason | null
  const rawNextAction = params.get('sso_action') as SsoNextAction | null
  const reason = rawReason && denialReasons.has(rawReason) ? rawReason : null
  const nextAction = rawNextAction && nextActions.has(rawNextAction) ? rawNextAction : null
  const authenticated = params.get('sso_authenticated') === 'true'
  return {
    code,
    message: reason ? denialMessages[reason] : message || fallbackMessages[code] || fallbackMessages.sso_failed,
    provider: params.get('sso_provider'),
    authenticated,
    reason,
    nextAction,
  }
}

function clearRoleInvitationSession() {
  window.sessionStorage.removeItem('role_invitation_kind')
  window.sessionStorage.removeItem('role_invitation_token')
}

function clearStudentInvitationSession() {
  window.sessionStorage.removeItem('student_invitation_token')
}

function clearStudentCompletionSession() {
  window.sessionStorage.removeItem('student_invitation_complete')
}

function getSignInContext() {
  const params = new URLSearchParams(window.location.search)
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))
  const client = parseClient(params.get('client'))
  const rawRoleInvitationKind = hashParams.get('role_invitation_kind')
    || params.get('role_invitation_kind')
    || window.sessionStorage.getItem('role_invitation_kind')
  const roleInvitationKind = rawRoleInvitationKind === 'librarian'
    ? 'librarian'
    : rawRoleInvitationKind === 'instructor'
      ? 'instructor'
      : null
  const roleInvitationToken = hashParams.get('role_invitation_token')
    || params.get('role_invitation_token')
    || window.sessionStorage.getItem('role_invitation_token')
  if (hashParams.get('role_invitation_token') && roleInvitationKind && roleInvitationToken) {
    window.sessionStorage.setItem('role_invitation_kind', roleInvitationKind)
    window.sessionStorage.setItem('role_invitation_token', roleInvitationToken)
    window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`)
  }
  const studentInvitationToken = hashParams.get('student_invitation_token')
    || window.sessionStorage.getItem('student_invitation_token')
  const studentInvitationComplete = hashParams.get('student_invitation_complete')
    || window.sessionStorage.getItem('student_invitation_complete')
  if (hashParams.get('student_invitation_token') && studentInvitationToken) {
    window.sessionStorage.setItem('student_invitation_token', studentInvitationToken)
  }
  if (hashParams.get('student_invitation_complete') && studentInvitationComplete) {
    window.sessionStorage.setItem('student_invitation_complete', studentInvitationComplete)
  }
  if (hashParams.get('student_invitation_token') || hashParams.get('student_invitation_complete')) {
    window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`)
  }
  return {
    portal: roleInvitationKind === 'librarian'
      ? 'librarian' as const
      : roleInvitationKind === 'instructor'
        ? 'teacher' as const
        : parsePortal(params.get('portal')),
    client,
    target: client === 'desktop' ? null : params.get('target'),
    ssoError: parseSsoError(params),
    linkToken: params.get('sso_link_token'),
    ssoEmail: params.get('sso_email'),
    ssoProvider: params.get('sso_provider'),
    roleInvitationKind,
    roleInvitationToken,
    studentInvitationToken,
    studentInvitationComplete,
  }
}

function buildLoginUrl(org: DiscoveryOrganization, context: ReturnType<typeof getSignInContext>): string {
  const loginPath = org.provider === 'openathens'
    ? '/auth/openathens/login'
    : `/auth/oidc/${encodeURIComponent(org.provider)}/login`
  const url = new URL(`${API_BASE_URL}${loginPath}`)
  url.searchParams.set('portal', context.portal)
  url.searchParams.set('client', context.client)
  url.searchParams.set('organizationId', org.id)
  if (context.target) url.searchParams.set('target', context.target)
  return url.href
}

function isTestOrganization(org: DiscoveryOrganization): boolean {
  return org.name.includes('[Test]')
}

function organizationMatches(org: DiscoveryOrganization, query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) return !isTestOrganization(org)
  const searchable = [org.name, ...org.domains].join(' ').toLowerCase()
  return searchable.includes(normalizedQuery)
}

async function readApiError(response: Response): Promise<string> {
  try {
    const payload = await response.json() as ApiErrorPayload
    return payload.message || payload.error || 'Request failed.'
  } catch {
    return 'Request failed.'
  }
}

function LudolioLogo() {
  return (
    <div className="flex justify-center">
      <img src="/logo.png" alt="Ludolio" className="h-12 w-auto" />
    </div>
  )
}

function AccountLinkFlow({
  linkToken,
  initialSsoEmail,
}: {
  linkToken: string
  initialSsoEmail: string | null
}) {
  const [status, setStatus] = useState<AccountLinkStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [mode, setMode] = useState<Exclude<SsoAccountLinkMode, 'loading' | 'expired'>>('choose')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function loadStatus() {
      setLoading(true)
      setError('')
      try {
        const url = new URL(`${API_BASE_URL}/auth/sso/account-link/status`)
        url.searchParams.set('linkToken', linkToken)
        const response = await fetch(url.href)
        const payload = await response.json() as { success?: boolean; data?: AccountLinkStatus; message?: string; error?: string }
        if (!response.ok || !payload.success || !payload.data) {
          throw new Error(payload.message || payload.error || 'This linking session is no longer valid.')
        }
        if (!cancelled) {
          setStatus(payload.data)
          setEmail(initialSsoEmail || '')
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Unable to continue account linking.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void loadStatus()
    return () => {
      cancelled = true
    }
  }, [linkToken, initialSsoEmail])

  async function finishWithRedirect(redirectUrl: string) {
    window.location.assign(redirectUrl)
  }

  async function handleCreateNew() {
    setSubmitting(true)
    setError('')
    try {
      const response = await fetch(`${API_BASE_URL}/auth/sso/account-link/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ linkToken }),
      })
      const payload = await response.json() as { success?: boolean; data?: { redirectUrl?: string }; message?: string; error?: string }
      if (!response.ok || !payload.success || !payload.data?.redirectUrl) {
        throw new Error(payload.message || payload.error || await readApiError(response))
      }
      await finishWithRedirect(payload.data.redirectUrl)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to create account.')
      setSubmitting(false)
    }
  }

  async function handleSendOtp(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    try {
      const response = await fetch(`${API_BASE_URL}/auth/sso/account-link/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ linkToken, email }),
      })
      const payload = await response.json() as { success?: boolean; message?: string; error?: string }
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || payload.error || 'Unable to send verification code.')
      }
      setMode('link-code')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to send verification code.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleConfirmLink(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    try {
      const response = await fetch(`${API_BASE_URL}/auth/sso/account-link/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ linkToken, email, token: code }),
      })
      const payload = await response.json() as { success?: boolean; data?: { redirectUrl?: string }; message?: string; error?: string }
      if (!response.ok || !payload.success || !payload.data?.redirectUrl) {
        throw new Error(payload.message || payload.error || 'Unable to link account.')
      }
      await finishWithRedirect(payload.data.redirectUrl)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to link account.')
      setSubmitting(false)
    }
  }

  const panelMode: SsoAccountLinkMode = loading ? 'loading' : !status ? 'expired' : mode

  return (
    <SsoAccountLinkPanel
      mode={panelMode}
      organizationName={status?.organizationName}
      ssoEmail={status?.ssoEmail}
      email={email}
      code={code}
      error={error}
      submitting={submitting}
      onEmailChange={setEmail}
      onCodeChange={setCode}
      onLinkExisting={() => setMode('link-email')}
      onCreateNew={() => void handleCreateNew()}
      onSendOtp={(event) => void handleSendOtp(event)}
      onConfirmLink={(event) => void handleConfirmLink(event)}
      onBackToChoose={() => setMode('choose')}
      onBackToEmail={() => setMode('link-email')}
    />
  )
}

function StudentDownloadActions() {
  const prefersMac = /Mac|iPhone|iPad/.test(navigator.userAgent)
  const downloads = prefersMac
    ? [
        { label: 'Download for macOS', href: 'https://api.ludolio.com/api/v1/app/update/download/mac', Icon: AppleIcon },
        { label: 'Download for Windows', href: 'https://api.ludolio.com/api/v1/app/update/download/windows', Icon: WindowsIcon },
      ]
    : [
        { label: 'Download for Windows', href: 'https://api.ludolio.com/api/v1/app/update/download/windows', Icon: WindowsIcon },
        { label: 'Download for macOS', href: 'https://api.ludolio.com/api/v1/app/update/download/mac', Icon: AppleIcon },
      ]

  return (
    <>
      <div className="grid gap-2 sm:grid-cols-2">
        {downloads.map(({ label, href, Icon }) => (
          <Button key={href} variant="outline" asChild>
            <a href={href} className="inline-flex items-center justify-center gap-2">
              <Icon className="h-4 w-4 shrink-0" />
              {label}
            </a>
          </Button>
        ))}
      </div>
      <p className="text-center text-xs text-slate-500">Download Ludolio on a Windows or Mac computer, then sign in with your institution.</p>
    </>
  )
}

function StudentInvitationFlow({
  token,
  ssoError,
  onAccepted,
}: {
  token: string
  ssoError: ReturnType<typeof getSignInContext>['ssoError']
  onAccepted: (accepted: boolean) => void
}) {
  const [status, setStatus] = useState<StudentInvitationStatus | null>(null)
  const [organization, setOrganization] = useState<DiscoveryOrganization | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [redirecting, setRedirecting] = useState(false)
  const [sendingCode, setSendingCode] = useState(false)
  const [codeSent, setCodeSent] = useState(false)
  const [code, setCode] = useState('')

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      setOrganization(null)
      setCodeSent(false)
      setCode('')
      try {
        const statusResponse = await fetch(`${API_BASE_URL}/auth/student-invitation/status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          cache: 'no-store',
          body: JSON.stringify({ token }),
        })
        const statusPayload = await statusResponse.json() as {
          success?: boolean
          data?: StudentInvitationStatus
          message?: string
          error?: string
        }
        if (!statusResponse.ok || !statusPayload.success || !statusPayload.data) {
          const message = statusPayload.message || statusPayload.error || 'This invitation is invalid or expired.'
          if (statusResponse.status === 401 || statusResponse.status === 404) {
            clearStudentInvitationSession()
          }
          throw new Error(message)
        }
        const invitationStatus = statusPayload.data
        if (invitationStatus.status === 'already_accepted') {
          clearStudentInvitationSession()
          if (!cancelled) {
            setStatus(invitationStatus)
            onAccepted(true)
          }
          return
        }

        let matchedOrganization: DiscoveryOrganization | null = null
        if (invitationStatus.authenticationMode !== 'otp_only') {
          try {
            const discoveryResponse = await fetch(`${API_BASE_URL}/auth/sso/discovery-organizations?portal=student`)
            const discoveryPayload = await discoveryResponse.json() as DiscoveryResponse
            if (discoveryResponse.ok && discoveryPayload.success) {
              matchedOrganization = discoveryPayload.data?.find(
                (item) => item.id === invitationStatus.organizationId,
              ) || null
            }
          } catch {
            matchedOrganization = null
          }
          if (invitationStatus.authenticationMode === 'sso_only' && !matchedOrganization) {
            throw new Error('Institutional sign-in is not configured for this invitation.')
          }
        }

        if (!cancelled) {
          setStatus(invitationStatus)
          setOrganization(matchedOrganization)
        }
      } catch (caught) {
        if (!cancelled) setError(caught instanceof Error ? caught.message : 'Unable to load this invitation.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [onAccepted, token])

  async function handleInstitutionalSignIn() {
    if (!status || status.status !== 'active' || !organization) return

    setRedirecting(true)
    setError('')
    try {
      const response = await fetch(`${API_BASE_URL}/auth/student-invitation/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, provider: organization.provider }),
      })
      const payload = await response.json() as {
        success?: boolean
        data?: { redirectUrl?: string }
        message?: string
        error?: string
      }
      if (!response.ok || !payload.success || !payload.data?.redirectUrl) {
        throw new Error(payload.message || payload.error || 'Unable to start institutional sign-in.')
      }
      window.location.assign(payload.data.redirectUrl)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to start institutional sign-in.')
      setRedirecting(false)
    }
  }

  async function handleSendOtp() {
    if (!status || status.status !== 'active') return

    setSendingCode(true)
    setError('')
    try {
      const response = await fetch(`${API_BASE_URL}/auth/student-invitation/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const payload = await response.json() as {
        success?: boolean
        message?: string
        error?: string
      }
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || payload.error || 'Unable to send an email code.')
      }
      setCodeSent(true)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to send an email code.')
    } finally {
      setSendingCode(false)
    }
  }

  async function handleVerifyOtp(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!status || status.status !== 'active') return

    setRedirecting(true)
    setError('')
    try {
      const response = await fetch(`${API_BASE_URL}/auth/student-invitation/verify-otp`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, code }),
      })
      const payload = await response.json() as {
        success?: boolean
        data?: { redirectUrl?: string }
        message?: string
        error?: string
      }
      if (!response.ok || !payload.success || !payload.data?.redirectUrl) {
        throw new Error(payload.message || payload.error || 'Unable to verify the email code.')
      }
      clearStudentInvitationSession()
      window.location.assign(payload.data.redirectUrl)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to verify the email code.')
      setRedirecting(false)
    }
  }

  if (loading || redirecting) {
    return (
      <div className="flex min-h-40 items-center justify-center" aria-label="Loading invitation">
        <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
      </div>
    )
  }
  if (!status) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-950" role="alert">
        {error || 'This invitation is invalid or expired.'}
      </div>
    )
  }
  if (status.status === 'already_accepted') {
    return <StudentDownloadActions />
  }

  const otpAvailable = status.authenticationMode === 'otp_only' || status.authenticationMode === 'otp_and_sso'
  const ssoAvailable = Boolean(organization)
  const hasChoice = otpAvailable && ssoAvailable

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
        <p className="font-semibold text-slate-950">{status.organizationName}</p>
        <p className="mt-1 text-sm text-slate-500">{status.invitedEmail}</p>
        <div className="mt-4 space-y-2">
          {status.units.map((unit) => (
            <p key={unit.id} className="text-sm text-slate-700">
              <strong>{unit.code}</strong>
              {' '}
              —
              {' '}
              {unit.name}
            </p>
          ))}
        </div>
      </div>
      {ssoError && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950" role="alert">
          {ssoError.message}
        </div>
      )}
      {error && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950" role="alert">
          {error}
        </div>
      )}
      {codeSent ? (
        <form className="space-y-3" onSubmit={(event) => void handleVerifyOtp(event)}>
          <label className="block text-sm font-medium text-slate-700" htmlFor="student-invitation-code">
            Email code
          </label>
          <Input
            id="student-invitation-code"
            autoComplete="one-time-code"
            inputMode="numeric"
            maxLength={6}
            pattern="[0-9]*"
            placeholder="6-digit code"
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
          />
          <div className="grid gap-2 sm:grid-cols-2">
            <Button type="submit" className="h-11" disabled={code.length !== 6}>
              Verify code
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-11"
              disabled={sendingCode}
              onClick={() => void handleSendOtp()}
            >
              {sendingCode ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send another code'}
            </Button>
          </div>
        </form>
      ) : (
        <div className={hasChoice ? 'grid gap-2 sm:grid-cols-2' : 'space-y-2'}>
          {otpAvailable && (
            <Button
              type="button"
              variant={hasChoice ? 'outline' : undefined}
              className="h-11 w-full rounded-lg"
              disabled={sendingCode}
              onClick={() => void handleSendOtp()}
            >
              {sendingCode ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign in with email code'}
            </Button>
          )}
          {ssoAvailable && (
            <Button
              type="button"
              variant={hasChoice ? 'outline' : undefined}
              className="h-11 w-full rounded-lg"
              onClick={() => void handleInstitutionalSignIn()}
            >
              Sign in with your institution
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

function StudentInvitationCompletionFlow({ token }: { token: string }) {
  const [completion, setCompletion] = useState<StudentInvitationCompletion | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    fetch(`${API_BASE_URL}/auth/student-invitation/completion`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(async (response) => {
        const payload = await response.json() as { data?: StudentInvitationCompletion; message?: string }
        if (!response.ok || !payload.data) throw new Error(payload.message || 'This completion link is invalid or expired.')
        clearStudentInvitationSession()
        clearStudentCompletionSession()
        setCompletion(payload.data)
      })
      .catch((caught) => {
        clearStudentCompletionSession()
        setError(caught instanceof Error ? caught.message : 'Unable to load your enrolment.')
      })
  }, [token])

  if (error) return <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-950">{error}</div>
  if (!completion) return <div className="flex min-h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>

  return (
    <div className="space-y-5">
      <div>
        <p className="text-lg font-semibold text-slate-950">You’re enrolled</p>
        <p className="mt-2 text-sm text-slate-600">Your Ludolio account is ready for {completion.organizationName}.</p>
      </div>
      <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-5">
        {completion.units.map((unit) => <p key={unit.id} className="text-sm text-slate-700"><strong>{unit.code}</strong> — {unit.name}</p>)}
      </div>
      <StudentDownloadActions />
    </div>
  )
}

function RoleInvitationFlow({ context }: { context: ReturnType<typeof getSignInContext> }) {
  const [status, setStatus] = useState<RoleInvitationStatus | null>(null)
  const [organization, setOrganization] = useState<DiscoveryOrganization | null>(null)
  const [loading, setLoading] = useState(true)
  const [redirecting, setRedirecting] = useState(false)
  const [sendingCode, setSendingCode] = useState(false)
  const [codeSent, setCodeSent] = useState(false)
  const [code, setCode] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false

    async function loadInvitation() {
      setLoading(true)
      setError('')
      setOrganization(null)
      setCodeSent(false)
      setCode('')
      if (!context.roleInvitationKind || !context.roleInvitationToken) {
        clearRoleInvitationSession()
        setError('This invitation link is invalid or expired.')
        setLoading(false)
        return
      }

      try {
        const statusResponse = await fetch(`${API_BASE_URL}/auth/role-invitation/status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          cache: 'no-store',
          body: JSON.stringify({
            kind: context.roleInvitationKind,
            token: context.roleInvitationToken,
          }),
        })
        const statusPayload = await statusResponse.json() as {
          success?: boolean
          data?: RoleInvitationStatus
          message?: string
          error?: string
        }
        if (!statusResponse.ok || !statusPayload.success || !statusPayload.data) {
          const message = statusPayload.message || statusPayload.error || 'This invitation link is invalid or expired.'
          if (statusResponse.status === 401 || statusResponse.status === 404) {
            clearRoleInvitationSession()
          }
          throw new Error(message)
        }
        const invitationStatus = statusPayload.data
        if (invitationStatus.status === 'already_accepted') {
          clearRoleInvitationSession()
          if (!cancelled) setRedirecting(true)
          window.location.replace(invitationStatus.redirectUrl)
          return
        }

        let matchedOrganization: DiscoveryOrganization | null = null
        if (invitationStatus.authenticationMode !== 'otp_only') {
          try {
            const discoveryUrl = new URL(`${API_BASE_URL}/auth/sso/discovery-organizations`)
            discoveryUrl.searchParams.set('portal', invitationStatus.kind === 'librarian' ? 'librarian' : 'teacher')
            const discoveryResponse = await fetch(discoveryUrl.href)
            const discoveryPayload = await discoveryResponse.json() as DiscoveryResponse
            if (discoveryResponse.ok && discoveryPayload.success) {
              matchedOrganization = discoveryPayload.data?.find(
                (item) => item.id === invitationStatus.organizationId,
              ) || null
            }
          } catch {
            matchedOrganization = null
          }
          if (invitationStatus.authenticationMode === 'sso_only' && !matchedOrganization) {
            throw new Error('Institutional sign-in is not configured for this invitation.')
          }
        }

        if (!cancelled) {
          setStatus(invitationStatus)
          setOrganization(matchedOrganization)
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Unable to load this invitation.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void loadInvitation()
    return () => {
      cancelled = true
    }
  }, [context])

  async function handleInstitutionalSignIn() {
    if (!status || status.status !== 'active' || !organization || !context.roleInvitationKind || !context.roleInvitationToken) return

    setRedirecting(true)
    setError('')
    try {
      const response = await fetch(`${API_BASE_URL}/auth/role-invitation/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: context.roleInvitationKind,
          token: context.roleInvitationToken,
          provider: organization.provider,
        }),
      })
      const payload = await response.json() as {
        success?: boolean
        data?: { redirectUrl?: string }
        message?: string
        error?: string
      }
      if (!response.ok || !payload.success || !payload.data?.redirectUrl) {
        throw new Error(payload.message || payload.error || 'Unable to start institutional sign-in.')
      }
      window.location.assign(payload.data.redirectUrl)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to start institutional sign-in.')
      setRedirecting(false)
    }
  }

  async function handleSendOtp() {
    if (!status || status.status !== 'active' || !context.roleInvitationKind || !context.roleInvitationToken) return

    setSendingCode(true)
    setError('')
    try {
      const response = await fetch(`${API_BASE_URL}/auth/role-invitation/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: context.roleInvitationKind,
          token: context.roleInvitationToken,
        }),
      })
      const payload = await response.json() as {
        success?: boolean
        message?: string
        error?: string
      }
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || payload.error || 'Unable to send an email code.')
      }
      setCodeSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to send an email code.')
    } finally {
      setSendingCode(false)
    }
  }

  async function handleVerifyOtp(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!status || status.status !== 'active' || !context.roleInvitationKind || !context.roleInvitationToken) return

    setRedirecting(true)
    setError('')
    try {
      const response = await fetch(`${API_BASE_URL}/auth/role-invitation/verify-otp`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: context.roleInvitationKind,
          token: context.roleInvitationToken,
          code,
        }),
      })
      const payload = await response.json() as {
        success?: boolean
        data?: { redirectUrl?: string }
        message?: string
        error?: string
      }
      if (!response.ok || !payload.success || !payload.data?.redirectUrl) {
        throw new Error(payload.message || payload.error || 'Unable to verify the email code.')
      }
      clearRoleInvitationSession()
      window.location.replace(payload.data.redirectUrl)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to verify the email code.')
      setRedirecting(false)
    }
  }

  if (loading || redirecting) {
    return (
      <div className="flex min-h-40 items-center justify-center" aria-label="Loading invitation">
        <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
      </div>
    )
  }

  if (status?.status === 'already_accepted') {
    return <div className="flex min-h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-500" /></div>
  }

  if (!status) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-950" role="alert">
        {error || 'This invitation link is invalid or expired.'}
      </div>
    )
  }

  const roleLabel = status.kind === 'librarian' ? 'organization administrator' : 'instructor'
  const otpAvailable = status.authenticationMode === 'otp_only' || status.authenticationMode === 'otp_and_sso'
  const ssoAvailable = Boolean(organization)
  const hasChoice = otpAvailable && ssoAvailable

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
        <p className="font-semibold text-slate-950">{status.organizationName}</p>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Accept your {roleLabel} invitation.
        </p>
        <p className="mt-2 text-sm text-slate-500">{status.invitedEmail}</p>
      </div>
      {context.ssoError && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950" role="alert">
          {context.ssoError.message}
        </div>
      )}
      {error && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950" role="alert">
          {error}
        </div>
      )}
      {codeSent ? (
        <form className="space-y-3" onSubmit={(event) => void handleVerifyOtp(event)}>
          <label className="block text-sm font-medium text-slate-700" htmlFor="role-invitation-code">
            Email code
          </label>
          <Input
            id="role-invitation-code"
            autoComplete="one-time-code"
            inputMode="numeric"
            maxLength={6}
            pattern="[0-9]*"
            placeholder="6-digit code"
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
          />
          <div className="grid gap-2 sm:grid-cols-2">
            <Button type="submit" className="h-11" disabled={code.length !== 6}>
              Verify code
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-11"
              disabled={sendingCode}
              onClick={() => void handleSendOtp()}
            >
              {sendingCode ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send another code'}
            </Button>
          </div>
        </form>
      ) : (
        <div className={hasChoice ? 'grid gap-2 sm:grid-cols-2' : 'space-y-2'}>
          {otpAvailable && (
            <Button
              type="button"
              variant={hasChoice ? 'outline' : undefined}
              className="h-11 w-full rounded-lg"
              disabled={sendingCode}
              onClick={() => void handleSendOtp()}
            >
              {sendingCode ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign in with email code'}
            </Button>
          )}
          {ssoAvailable && (
            <Button
              type="button"
              variant={hasChoice ? 'outline' : undefined}
              className="h-11 w-full rounded-lg"
              onClick={() => void handleInstitutionalSignIn()}
            >
              Sign in with your institution
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

function InstitutionPicker({ context }: { context: ReturnType<typeof getSignInContext> }) {
  const ssoError = context.ssoError
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [organizations, setOrganizations] = useState<DiscoveryOrganization[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [redirectingOrgId, setRedirectingOrgId] = useState<string | null>(null)
  const [ssoErrorDismissed, setSsoErrorDismissed] = useState(false)

  const visibleOrganizations = useMemo(
    () => organizations.filter((org) => organizationMatches(org, query)),
    [organizations, query],
  )

  useEffect(() => {
    let cancelled = false

    async function loadOrganizations() {
      setLoading(true)
      setError('')

      try {
        const url = new URL(`${API_BASE_URL}/auth/sso/discovery-organizations`)
        url.searchParams.set('portal', context.portal)
        const response = await fetch(url.href)
        const payload = await response.json() as DiscoveryResponse

        if (!response.ok || !payload.success || !payload.data) {
          throw new Error(payload.message || payload.error || 'Unable to load institutions.')
        }

        if (!cancelled) setOrganizations(payload.data)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unable to load institutions.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void loadOrganizations()

    return () => {
      cancelled = true
    }
  }, [context.portal])

  function handleSignIn(org: DiscoveryOrganization) {
    setRedirectingOrgId(org.id)
    window.location.assign(buildLoginUrl(org, context))
  }

  useEffect(() => {
    if (!ssoError) return
    const url = new URL(window.location.href)
    url.searchParams.delete('sso_error')
    url.searchParams.delete('sso_provider')
    url.searchParams.delete('sso_message')
    url.searchParams.delete('sso_authenticated')
    url.searchParams.delete('sso_reason')
    url.searchParams.delete('sso_action')
    window.history.replaceState({}, '', url.href)
  }, [ssoError])

  return (
    <>
      {ssoError && !ssoErrorDismissed && (
        ssoError.authenticated ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-left text-amber-950" role="alert">
            <p className="text-base font-semibold">You have successfully logged in</p>
            <p className="mt-2 text-sm leading-6">{ssoError.message}</p>
            {ssoError.nextAction && (
              <p className="mt-2 text-sm font-medium">{denialGuidance[ssoError.nextAction]}</p>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                className="bg-white"
                onClick={() => {
                  setSsoErrorDismissed(true)
                  window.setTimeout(() => searchInputRef.current?.focus(), 0)
                }}
              >
                Choose another institution
              </Button>
              {ssoError.nextAction === 'contact_ludolio' && (
                <Button type="button" variant="ghost" asChild>
                  <a href={`mailto:${LUDOLIO_SUPPORT_EMAIL}`}>Contact support</a>
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-left text-sm text-amber-900" role="alert">
            <p className="font-semibold">{ssoError.message}</p>
            {ssoError.provider && (
              <p className="mt-2 text-xs uppercase tracking-wide text-amber-700">Provider: {ssoError.provider}</p>
            )}
          </div>
        )
      )}

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          ref={searchInputRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search by institution name or domain"
          className="h-12 rounded-xl pl-10 text-base"
          aria-label="Search institutions"
        />
      </div>

      <div className="min-h-72 space-y-3">
        {loading ? (
          <div className="space-y-3" aria-label="Loading institutions">
            {[0, 1, 2].map((item) => (
              <div key={item} className="h-16 animate-pulse rounded-xl bg-slate-100" />
            ))}
          </div>
        ) : error ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <p className="font-semibold">We could not load the institution list.</p>
            <p className="mt-1 text-amber-800">{error}</p>
            <Button type="button" variant="outline" className="mt-4 bg-white" onClick={() => window.location.reload()}>
              Try again
            </Button>
          </div>
        ) : visibleOrganizations.length > 0 ? (
          <div className="space-y-3">
            {visibleOrganizations.map((org) => (
              <div
                key={`${org.provider}:${org.id}`}
                className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 transition hover:border-[hsl(var(--color-primary)/0.45)] sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--color-primary)/0.10)] text-[hsl(var(--color-primary))]">
                    <Building2 className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-slate-950">{org.name}</p>
                  </div>
                </div>
                <Button
                  type="button"
                  className="rounded-lg sm:w-28"
                  disabled={Boolean(redirectingOrgId)}
                  onClick={() => handleSignIn(org)}
                >
                  {redirectingOrgId === org.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Sign in'
                  )}
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-700">
            <p className="text-base font-semibold text-slate-950">We could not find your organization.</p>
            <p className="mt-2 leading-6">
              Your institution may not have an active Ludolio subscription yet. Please contact your institution
              or Ludolio support for assistance.
            </p>
          </div>
        )}
      </div>
    </>
  )
}

function App() {
  const context = useMemo(() => getSignInContext(), [])
  const [studentInvitationAccepted, setStudentInvitationAccepted] = useState(false)
  const isAccountLink = Boolean(context.linkToken)
  const isRoleInvitation = Boolean(context.roleInvitationKind && context.roleInvitationToken)
  const isStudentInvitation = Boolean(context.studentInvitationToken)
  const isStudentCompletion = Boolean(context.studentInvitationComplete)

  useEffect(() => {
    if (!context.ssoError) return
    const url = new URL(window.location.href)
    for (const key of ['sso_error', 'sso_provider', 'sso_message', 'sso_authenticated', 'sso_reason', 'sso_action']) {
      url.searchParams.delete(key)
    }
    window.history.replaceState({}, '', url.href)
  }, [context.ssoError])

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[hsl(var(--color-background))] px-4 py-8 text-[hsl(var(--color-foreground))]">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_15%_15%,hsl(var(--color-primary)/0.14),transparent_30%),radial-gradient(circle_at_85%_10%,hsl(var(--color-primary)/0.08),transparent_28%),linear-gradient(180deg,hsl(var(--color-muted)/0.45),transparent_42%)]" />

      <Card className="relative w-full max-w-lg border-slate-200 bg-white/95 shadow-2xl shadow-slate-950/10 backdrop-blur">
        <CardHeader className="space-y-5 p-6 text-center sm:p-8">
          <LudolioLogo />
          <CardTitle className="text-2xl tracking-[-0.02em]">
            {isAccountLink
              ? 'Connect your Ludolio account'
              : studentInvitationAccepted
                ? 'You have already joined Ludolio.'
              : isStudentCompletion
                ? 'Your Ludolio account is ready'
                : isRoleInvitation || isStudentInvitation
                ? 'Accept your Ludolio invitation'
                : 'Sign in with your institution'}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-5 p-6 pt-0 sm:p-8 sm:pt-0">
          {isAccountLink && context.linkToken ? (
            <AccountLinkFlow linkToken={context.linkToken} initialSsoEmail={context.ssoEmail} />
          ) : isStudentCompletion && context.studentInvitationComplete ? (
            <StudentInvitationCompletionFlow token={context.studentInvitationComplete} />
          ) : isStudentInvitation && context.studentInvitationToken ? (
            <StudentInvitationFlow
              token={context.studentInvitationToken}
              ssoError={context.ssoError}
              onAccepted={setStudentInvitationAccepted}
            />
          ) : isRoleInvitation ? (
            <RoleInvitationFlow context={context} />
          ) : (
            <InstitutionPicker context={context} />
          )}
        </CardContent>
      </Card>
    </main>
  )
}

export default App
